import React, { useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, MenuItem, Popover } from '@tempmee/react';
import { OutlineChevronDownIcon } from '@tempmee/react-icons';
import { getStatusOptions, MEMBERSHIP_STATUS_LABELS } from '../../../../domain/Awards';

/**
 * Status control for one roster row.
 *
 * The server enforces a legal-transition matrix, so a free dropdown would hand
 * admins 409s. Every status is rendered — illegal ones greyed with the reason —
 * rather than filtered out, so the vocabulary stays visible and the refusal is
 * explained rather than mysterious.
 *
 * Built from `Menu` + `MenuItem` rather than `InputSelectField` because that
 * component's options carry no per-option disabled state or description. Note
 * DS `Menu` is presentational only ("positioning/anchoring is left to the
 * consumer"), so the open/close state lives here.
 */
export const MembershipStatusSelect = ({ row, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const baseId = useId();

  const options = getStatusOptions(row.status);
  // Labelled from the shared vocabulary, not row.statusLabel, so a stale label
  // on the row cannot desync the control from the status it is editing.
  const currentLabel = MEMBERSHIP_STATUS_LABELS[row.status] ?? row.status;

  const select = (option) => {
    if (option.disabled) return;

    onChange(option.value);
    setIsOpen(false);
  };

  return (
    <div>
      <Button
        ref={triggerRef}
        variant="outlined"
        size="small"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Change status (${currentLabel})`}
        onClick={() => setIsOpen((open) => !open)}
        // Without a chevron this is an outlined pill with no hint it opens.
        endIcon={<OutlineChevronDownIcon aria-hidden className="size-4" />}
      >
        {currentLabel}
      </Button>

      {/*
        `Popover`, not a bare `Menu` beside the trigger: TableCard is
        `overflow: hidden` and would clip the menu — on a short roster that hid
        Winner, Not Selected and Not Entered entirely — while TableContainer's
        `overflow: auto` gave the table a scrollbar it did not need.

        Popover portals out of both, and its useDismiss covers Escape and
        outside-click. It stays mounted for a 150ms exit transition, which is
        why its tests await removal rather than asserting synchronously.
      */}
      <Popover
        open={isOpen}
        anchorEl={triggerRef.current}
        onClose={() => setIsOpen(false)}
        placement="bottom-start"
        surface="menu"
      >
        <div role="listbox" aria-label="Membership status">
          {options.map((option) => {
            const reasonId = option.disabledReason
              ? `${baseId}-${option.value}-reason`
              : undefined;

            return (
              <React.Fragment key={option.value}>
                <MenuItem
                  role="option"
                  aria-selected={option.value === row.status}
                  aria-disabled={option.disabled ? 'true' : undefined}
                  aria-describedby={reasonId}
                  disabled={option.disabled}
                  selected={option.value === row.status}
                  onClick={() => select(option)}
                >
                  {option.label}
                </MenuItem>

                {/* Visually hidden so the reason reaches assistive tech
                    without crowding the menu; also what aria-describedby
                    resolves to. */}
                {reasonId ? (
                  <span id={reasonId} className="sr-only">
                    {option.disabledReason}
                  </span>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </Popover>
    </div>
  );
};

MembershipStatusSelect.propTypes = {
  row: PropTypes.shape({
    userId: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  /** True while a status change is in flight. */
  disabled: PropTypes.bool,
};

MembershipStatusSelect.defaultProps = { disabled: false };
